import React, { useEffect, useRef, useState, useCallback } from "react";
import OfferBottomSheet from "../components/OfferBottomSheet";
import BottomNavigation from "../components/BottomNavigation";
import { motion } from "framer-motion";
import { obtenerOfertas, registrarAlcanceOferta } from "../api";

const Ofertas = () => {
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [offersData, setOffersData] = useState([]);
  const [visibleOffers, setVisibleOffers] = useState([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const observerRefs = useRef({});
  const sentinelRef = useRef(null);
  const timers = useRef({});
  const userId = JSON.parse(localStorage.getItem("userData"))?.id || null;

  useEffect(() => {
    if (loading) return;
  
    const sentinelObserver = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry.isIntersecting && page * 3 < offersData.length) {
        setPage(prev => prev + 1);
      }
    }, {
      rootMargin: '100px',
      threshold: 0.1
    });
  
    if (sentinelRef.current) {
      sentinelObserver.observe(sentinelRef.current);
    }
  
    return () => sentinelObserver.disconnect();
  }, [loading, page, offersData.length]);

  useEffect(() => {
    if (
      !loading &&
      page * 3 < offersData.length &&
      document.body.scrollHeight <= window.innerHeight
    ) {
      setPage(prev => prev + 1);
    }
  }, [visibleOffers, loading, offersData, page]);  

  const loadMore = useCallback(() => {
    const next = offersData.slice(0, page * 3);
    setVisibleOffers(next);
  }, [offersData, page]);

  const fetchOfertas = async () => {
    setLoading(true);
    const ofertas = await obtenerOfertas();
    setOffersData(ofertas);
    setLoading(false);
  };

  useEffect(() => {
    fetchOfertas();
  }, []);

  useEffect(() => {
    loadMore();
  }, [offersData, page, loadMore]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          const offerId = entry.target.getAttribute("data-id");
          const lat = localStorage.getItem("userLat");
          const lang = localStorage.getItem("userLng");
          if (entry.isIntersecting) {
            timers.current[offerId] = setTimeout(() => {
              registrarAlcanceOferta(parseInt(offerId), userId, lat, lang);
            }, 1500);
          } else {
            clearTimeout(timers.current[offerId]);
          }
        });
      },
      { threshold: 0.6 }
    );

    visibleOffers.forEach(offer => {
      if (observerRefs.current[offer.id_oferta]) {
        observer.observe(observerRefs.current[offer.id_oferta]);
        observerRefs.current[offer.id_oferta]?.dispatchEvent(new Event('scroll'));
      }
    });

    return () => {
      observer.disconnect();
      Object.values(timers.current).forEach(clearTimeout);
    };
  }, [visibleOffers, userId]);

  const handleScroll = () => {
    if (window.innerHeight + window.scrollY >= document.body.offsetHeight - 100) {
      if (page * 3 < offersData.length) {
        setPage(prev => prev + 1);
      }
    }
  };

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [offersData, page]);

  const openOfferSheet = (offer) => {
    setSelectedOffer(offer);
  };

  return (
    <>
      <motion.div
        initial={{ y: "100%", opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: "100%", opacity: 0 }}
        transition={{ duration: 0.7, ease: "easeInOut" }}
      >
        <div className="app-container">
          <div className="offers-container">
            <h2 className="offers-title">Ofertas disponibles</h2>

            {loading ? (
              <div style={{ textAlign: "center", marginTop: 80 }}>
                <div className="custom-spinner"></div>
              </div>
            ) : visibleOffers.length > 0 ? (
              <>
                {visibleOffers.map((offer) => (
                  <div
                    key={offer.id_oferta}
                    data-id={offer.id_oferta}
                    ref={el => (observerRefs.current[offer.id_oferta] = el)}
                    className="offer-card"
                    onClick={() => openOfferSheet(offer)}
                  >
                    <img className="offer-img" src={offer.image} alt={offer.title} />
                    <div className="offer-details">
                      <b className="business-nm">
                        {offer.businessName}
                        <img
                          src="/assets/images/verified.svg"
                          className="verifiedUser"
                          alt="Verificado"
                          style={{ width: 300 }}
                        />
                      </b>
                      <h3 className="offer-title">{offer.title}</h3>
                      <p className="offer-expiry">{offer.expiry}</p>
                    </div>
                  </div>
                ))}
                {page * 3 < offersData.length && (
                  <div style={{ textAlign: "center", marginTop: 40, marginBottom:60 }}>
                    <div className="custom-spinner"></div>
                  </div>
                )}
              </>
            ) : (
              <div className="no-data-wrapper" style={{ height: 300, marginTop: 95 }}>
                <div className="no-data">
                  <img src="/assets/images/empty.svg" alt="Sin referidos" />
                  <p>Sin ofertas disponibles, vuelve pronto.</p>
                </div>
              </div>
            )}

          </div>
          <OfferBottomSheet
            offer={selectedOffer}
            visible={!!selectedOffer}
            onClose={() => setSelectedOffer(null)}
          />
        </div>
      </motion.div>
      <div ref={sentinelRef} style={{ height: 1 }}></div>
      <BottomNavigation />
    </>
  );
};

export default Ofertas;
